// Data layer — Firestore live subscriptions + writes.
//
// All field names follow the LOCKED decisions in BUILD_HANDOFF.md:
//   status: 'todo' | 'in_progress' | 'done'
//   assigneeUid (Auth UID) · dueDate (Timestamp) · labelIds (-> labels coll.)
// Comments & activity are subcollections under each task.

import {
  collection,
  collectionGroup,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  increment,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  getDoc,
  Timestamp,
  runTransaction,
} from 'firebase/firestore'
import { db } from './firebase.js'
import { initialsOf, paletteFor, DEFAULT_STATUSES, DEFAULT_ENGAGEMENT_STAGES, CORE_PROPERTY_DEFS, normalizeEmail, normalizeDomain, objToken, engagementType } from './format.js'

const snapToDocs = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))

// ── Live subscriptions ──────────────────────────────────────────────────────

/** Tasks in a project, ordered by fractional `order`. */
export function watchProjectTasks(projectId, cb) {
  const q = query(
    collection(db, 'tasks'),
    where('projectId', '==', projectId),
    orderBy('order', 'asc'),
  )
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

/** Unfiltered tasks across all projects — used by the Home dashboard for org-wide metrics. */
export function watchAllTasks(cb) {
  return onSnapshot(collection(db, 'tasks'), (snap) => cb(snapToDocs(snap)))
}

/** Tasks assigned to a user across all projects. */
export function watchMyTasks(uid, cb) {
  const q = query(
    collection(db, 'tasks'),
    where('assigneeUid', '==', uid),
    orderBy('dueDate', 'asc'),
  )
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

/** A task's comments, oldest-first. */
export function watchComments(taskId, cb) {
  const q = query(
    collection(db, 'tasks', taskId, 'comments'),
    orderBy('createdAt', 'asc'),
  )
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

/** A task's activity feed, newest-first. */
export function watchActivity(taskId, cb) {
  const q = query(
    collection(db, 'tasks', taskId, 'activity'),
    orderBy('createdAt', 'desc'),
  )
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

/** Projects the user is a member of. */
export function watchMyProjects(uid, cb) {
  const q = query(
    collection(db, 'projects'),
    where('memberUids', 'array-contains', uid),
    orderBy('createdAt', 'asc'),
  )
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

/** Workspace labels. */
export function watchLabels(cb) {
  const q = query(collection(db, 'labels'), orderBy('name', 'asc'))
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

export async function createLabel(label, actorUid) {
  const ref = await addDoc(collection(db, 'labels'), { name: label.name || 'New label', color: label.color || '#7A7568', createdBy: actorUid || null })
  return ref.id
}

export async function updateLabel(id, patch) { await updateDoc(doc(db, 'labels', id), patch) }

export async function deleteLabel(id) { await deleteDoc(doc(db, 'labels', id)) }

/** Workspace users (assignee + mention pickers). */
export function watchUsers(cb) {
  const q = query(collection(db, 'users'), orderBy('name', 'asc'))
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

/** A user's notifications, newest-first (capped). */
export function watchMyNotifications(uid, cb) {
  const q = query(
    collection(db, 'notifications'),
    where('forUid', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(40),
  )
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

// ── Writes: tasks ─────────────────────────────────────────────────────────────

const DEFAULT_ORDER_STEP = 1000

/** Create a task. `due` may be a JS Date or null. */
export async function createTask(task, actorUid) {
  const ref = await addDoc(collection(db, 'tasks'), {
    projectId: task.projectId,
    title: task.title,
    description: task.description || '',
    assigneeUid: task.assigneeUid || null,
    status: task.status || 'todo',
    dueDate: task.dueDate ? Timestamp.fromDate(task.dueDate) : null,
    reminderAt: task.reminderAt ? Timestamp.fromDate(task.reminderAt) : null,
    labelIds: task.labelIds || [],
    order: typeof task.order === 'number' ? task.order : Date.now(),
    commentCount: 0,
    source: task.source || null,
    phase: task.phase || null,
    engagementId: task.engagementId || null,
    createdAt: serverTimestamp(),
    createdBy: actorUid || null,
  })
  await addActivity(ref.id, 'Task created', actorUid)
  return ref.id
}

/** Patch arbitrary task fields. Pass `dueDate` as a JS Date or null. */
export async function updateTask(taskId, patch) {
  const data = { ...patch }
  if ('dueDate' in patch) {
    data.dueDate = patch.dueDate ? Timestamp.fromDate(patch.dueDate) : null
  }
  if ('reminderAt' in patch) {
    data.reminderAt = patch.reminderAt ? Timestamp.fromDate(patch.reminderAt) : null
  }
  await updateDoc(doc(db, 'tasks', taskId), data)
}

export async function setTaskStatus(taskId, status, actorUid, order, statusName) {
  const patch = { status }
  if (typeof order === 'number') patch.order = order
  await updateDoc(doc(db, 'tasks', taskId), patch)
  await addActivity(taskId, `Status changed to ${statusName || status}`, actorUid)
}

export async function setTaskOrder(taskId, order) {
  await updateDoc(doc(db, 'tasks', taskId), { order })
}

export async function setAssignee(taskId, assigneeUid, actorUid, assigneeName) {
  await updateDoc(doc(db, 'tasks', taskId), { assigneeUid })
  await addActivity(taskId, `Assigned to ${assigneeName || 'someone'}`, actorUid)
}

export async function toggleLabel(taskId, labelIds) {
  await updateDoc(doc(db, 'tasks', taskId), { labelIds })
}

/** Delete a task and its comments/activity subcollections. */
export async function deleteTask(taskId) {
  const batch = writeBatch(db)
  for (const sub of ['comments', 'activity']) {
    const snap = await getDocs(collection(db, 'tasks', taskId, sub))
    snap.forEach((d) => batch.delete(d.ref))
  }
  batch.delete(doc(db, 'tasks', taskId))
  await batch.commit()
}

// ── Writes: comments + activity ───────────────────────────────────────────────

export async function addComment(taskId, { authorUid, text, mentions }) {
  await addDoc(collection(db, 'tasks', taskId, 'comments'), {
    authorUid,
    text,
    mentions: mentions || [],
    createdAt: serverTimestamp(),
  })
  // Keep the denormalized count in sync for List/Board badges.
  await updateDoc(doc(db, 'tasks', taskId), { commentCount: increment(1) })
}

// ── Writes: notifications (in-app; no Cloud Function) ─────────────────────────

/** Create a notification for `forUid`. No-op when notifying yourself. */
export async function createNotification({ forUid, actorUid, type, taskId, projectId, text }) {
  if (!forUid || forUid === actorUid) return
  await addDoc(collection(db, 'notifications'), {
    forUid,
    actorUid: actorUid || null,
    type, // 'mention' | 'assignment'
    taskId: taskId || null,
    projectId: projectId || null,
    text: text || '',
    read: false,
    createdAt: serverTimestamp(),
  })
}

export async function markNotificationRead(id) {
  await updateDoc(doc(db, 'notifications', id), { read: true })
}

export async function markAllNotificationsRead(ids) {
  if (!ids || !ids.length) return
  const batch = writeBatch(db)
  ids.forEach((id) => batch.update(doc(db, 'notifications', id), { read: true }))
  await batch.commit()
}

export async function addActivity(taskId, text, actorUid) {
  await addDoc(collection(db, 'tasks', taskId, 'activity'), {
    text,
    actorUid: actorUid || null,
    createdAt: serverTimestamp(),
  })
}

// ── Writes: projects ──────────────────────────────────────────────────────────

export async function createProject({ name, subtitle, memberUids }, actorUid) {
  const ref = await addDoc(collection(db, 'projects'), {
    name,
    subtitle: subtitle || '',
    memberUids: memberUids && memberUids.length ? memberUids : [actorUid],
    createdAt: serverTimestamp(),
    createdBy: actorUid || null,
  })
  return ref.id
}

// ── Writes: team roster (invites) + project membership ────────────────────────

/**
 * Invite a teammate who hasn't signed in yet. Creates a PENDING user doc; they
 * appear in assignee/member pickers immediately and get linked to their real
 * account on first sign-in (see reconcilePendingUser). Idempotent on email —
 * returns the existing user's id if one already exists for that email.
 */
export async function inviteUser({ name, email }, actorUid) {
  const cleanEmail = (email || '').trim().toLowerCase()
  const cleanName = (name || '').trim() || cleanEmail
  const existing = await getDocs(query(collection(db, 'users'), where('email', '==', cleanEmail)))
  if (!existing.empty) return existing.docs[0].id
  const palette = paletteFor(cleanEmail)
  const ref = await addDoc(collection(db, 'users'), {
    name: cleanName,
    email: cleanEmail,
    initials: initialsOf(cleanName),
    color: palette.color,
    ink: palette.ink,
    pending: true,
    invitedBy: actorUid || null,
    createdAt: serverTimestamp(),
  })
  await updateDoc(ref, { uid: ref.id })
  return ref.id
}

/** Replace a project's member list (uids — may include pending invite ids). */
export async function setProjectMembers(projectId, memberUids) {
  await updateDoc(doc(db, 'projects', projectId), { memberUids })
}

/** Rename / re-describe a project. */
export async function updateProject(projectId, patch) {
  await updateDoc(doc(db, 'projects', projectId), patch)
}

/** Delete a project and all of its tasks. */
export async function deleteProject(projectId) {
  const tasks = await getDocs(query(collection(db, 'tasks'), where('projectId', '==', projectId)))
  // Delete each task (with its subcollections) then the project doc.
  for (const t of tasks.docs) {
    await deleteTask(t.id)
  }
  await deleteDoc(doc(db, 'projects', projectId))
}

/** Update the signed-in user's own profile fields (name / avatar color). */
export async function updateUserProfile(uid, patch) {
  await updateDoc(doc(db, 'users', uid), patch)
}

/**
 * On first sign-in, link a pre-invited (pending) roster entry to the real Auth
 * account: copy its profile, re-point tasks assigned to it and projects it
 * belonged to, then delete the placeholder. Returns the merged profile or null.
 * Runs client-side — no Cloud Function required.
 */
export async function reconcilePendingUser(fbUser) {
  const email = (fbUser.email || '').toLowerCase()
  if (!email) return null
  const matches = await getDocs(query(collection(db, 'users'), where('email', '==', email)))
  const pending = matches.docs.find((d) => d.id !== fbUser.uid && d.data().pending === true)
  if (!pending) return null
  const pendingId = pending.id
  const data = pending.data()

  // Create/merge the real user doc from the invited profile. The name follows
  // the Google account people sign in with, not the roster spelling (Robert,
  // 2026-10-11); the roster name is only a fallback when Google has none.
  const name = fbUser.displayName || data.name || email
  await setDoc(
    doc(db, 'users', fbUser.uid),
    {
      uid: fbUser.uid,
      name,
      email,
      initials: initialsOf(name),
      color: data.color,
      ink: data.ink,
      // Roster details the Team page shows; Google sign-in carries none of them.
      ...Object.fromEntries(['role', 'phone', 'altEmail', 'timeZone'].filter((f) => data[f]).map((f) => [f, data[f]])),
      pending: false,
    },
    { merge: true },
  )

  // Re-point everything that referenced the placeholder, then delete it.
  const [assigned, inProjects, contacts, companies, engagements] = await Promise.all([
    getDocs(query(collection(db, 'tasks'), where('assigneeUid', '==', pendingId))),
    getDocs(query(collection(db, 'projects'), where('memberUids', 'array-contains', pendingId))),
    getDocs(query(collection(db, 'contacts'), where('ownerUid', '==', pendingId))),
    getDocs(query(collection(db, 'companies'), where('ownerUid', '==', pendingId))),
    getDocs(query(collection(db, 'engagements'), where('ownerUid', '==', pendingId))),
  ])

  const ops = []
  assigned.forEach((t) => ops.push((b) => b.update(t.ref, { assigneeUid: fbUser.uid })))
  inProjects.forEach((p) => {
    const arr = (p.data().memberUids || []).filter((x) => x !== pendingId)
    if (!arr.includes(fbUser.uid)) arr.push(fbUser.uid)
    ops.push((b) => b.update(p.ref, { memberUids: arr }))
  })
  ops.push((b) => b.delete(doc(db, 'users', pendingId)))

  // CRM collections are `stampedBySelf()` in firestore.rules, so each re-point
  // must carry updatedBy — an ownerUid-only patch is denied. They also run as a
  // separate best-effort pass: a CRM failure must never roll back the placeholder
  // delete above, or the invitee loses their profile and re-fails every login.
  const crmOps = []
  const stamp = { ownerUid: fbUser.uid, updatedBy: fbUser.uid, updatedAt: serverTimestamp() }
  ;[contacts, companies, engagements].forEach((snap) =>
    snap.forEach((d) => crmOps.push((b) => b.update(d.ref, stamp))))

  const CHUNK_SIZE = 400
  const commitChunked = async (list) => {
    for (let i = 0; i < list.length; i += CHUNK_SIZE) {
      const batch = writeBatch(db)
      list.slice(i, i + CHUNK_SIZE).forEach((fn) => fn(batch))
      await batch.commit()
    }
  }
  await commitChunked(ops)
  try {
    await commitChunked(crmOps)
  } catch (err) {
    console.warn('reconcilePendingUser: CRM ownership re-point failed', err)
  }

  return { name, initials: initialsOf(name), color: data.color, ink: data.ink }
}

/** Upsert the signed-in user's profile doc (called once after sign-in). */
export async function upsertUserProfile(user, extra = {}) {
  await setDoc(
    doc(db, 'users', user.uid),
    {
      uid: user.uid,
      name: user.displayName || user.email,
      email: user.email,
      ...(user.photoURL ? { photoURL: user.photoURL } : {}),
      ...extra,
    },
    { merge: true },
  )
}

// ── Writes/reads: tools & links (editable reference) ──────────────────────────

export function watchTools(cb) {
  const q = query(collection(db, 'tools'), orderBy('name', 'asc'))
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

export async function createTool(tool, actorUid) {
  const ref = await addDoc(collection(db, 'tools'), {
    name: tool.name || '',
    kind: tool.kind || 'tool',
    useFor: tool.useFor || '',
    owner: tool.owner || '',
    status: tool.status || '',
    url: tool.url || '',
    notes: tool.notes || '',
    updatedBy: actorUid || null,
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateTool(id, patch, actorUid) {
  await updateDoc(doc(db, 'tools', id), { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() })
}

export async function deleteTool(id) {
  await deleteDoc(doc(db, 'tools', id))
}

// ── Writes/reads: team dates / OOO ────────────────────────────────────────────

export function watchTeamDates(cb) {
  const q = query(collection(db, 'teamDates'), orderBy('start', 'asc'))
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

export async function createTeamDate(d, actorUid) {
  const ref = await addDoc(collection(db, 'teamDates'), {
    who: d.who || '',
    label: d.label || '',
    start: d.start ? Timestamp.fromDate(d.start) : null,
    end: d.end ? Timestamp.fromDate(d.end) : null,
    note: d.note || '',
    calendarEventId: d.calendarEventId || null,
    updatedBy: actorUid || null,
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateTeamDate(id, patch, actorUid) {
  const data = { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() }
  if ('start' in patch) data.start = patch.start ? Timestamp.fromDate(patch.start) : null
  if ('end' in patch) data.end = patch.end ? Timestamp.fromDate(patch.end) : null
  await updateDoc(doc(db, 'teamDates', id), data)
}

export async function deleteTeamDate(id) {
  await deleteDoc(doc(db, 'teamDates', id))
}

// ── Reads/writes: content pages (Playbook, etc.) ──────────────────────────────

export function watchPage(slug, cb) {
  return onSnapshot(doc(db, 'pages', slug), (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null))
}

export async function savePage(slug, body, actorUid) {
  await setDoc(doc(db, 'pages', slug), { body: body || '', updatedBy: actorUid || null, updatedAt: serverTimestamp() }, { merge: true })
}

// ── Reads/writes: canned email templates (register item 9) ───────────────────
// One shared, team-editable list. `types` scopes a template to engagement types;
// empty or absent means global, which is the common case and the editor default.

export function watchEmailTemplates(cb) {
  return onSnapshot(query(collection(db, 'emailTemplates'), orderBy('name', 'asc')), (snap) => cb(snapToDocs(snap)))
}

export async function createEmailTemplate(t, actorUid) {
  const ref = await addDoc(collection(db, 'emailTemplates'), {
    name: t.name || 'Untitled template',
    subject: t.subject || '',
    body: t.body || '',
    types: Array.isArray(t.types) ? t.types : [],
    // Templates v2 — all optional: workflow grouping + default Cc/Bcc.
    workflow: t.workflow || '',
    step: t.step || '',
    order: typeof t.order === 'number' && Number.isFinite(t.order) ? t.order : null,
    cc: Array.isArray(t.cc) ? t.cc : [],
    bcc: Array.isArray(t.bcc) ? t.bcc : [],
    createdBy: actorUid || null,
    createdAt: serverTimestamp(),
    updatedBy: actorUid || null,
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateEmailTemplate(id, patch, actorUid) {
  await updateDoc(doc(db, 'emailTemplates', id), { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() })
}

export async function deleteEmailTemplate(id) {
  await deleteDoc(doc(db, 'emailTemplates', id))
}

// ── Reads/writes: custom statuses (board columns / list sections) ─────────────

export function watchStatuses(cb) {
  const q = query(collection(db, 'statuses'), orderBy('order', 'asc'))
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

export async function createStatus(s, actorUid) {
  const ref = await addDoc(collection(db, 'statuses'), {
    name: s.name || 'New status',
    dot: s.dot || '#7A7568',
    color: s.color || '#7A7568',
    order: typeof s.order === 'number' ? s.order : Date.now(),
    done: !!s.done,
    updatedBy: actorUid || null,
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateStatus(id, patch, actorUid) {
  await updateDoc(doc(db, 'statuses', id), { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() })
}

export async function deleteStatus(id) {
  await deleteDoc(doc(db, 'statuses', id))
}

/** Seed the three default statuses (idempotent) when the collection is empty. */
export async function seedDefaultStatuses(actorUid) {
  const existing = await getDocs(collection(db, 'statuses'))
  if (!existing.empty) return
  const batch = writeBatch(db)
  DEFAULT_STATUSES.forEach((s) => batch.set(doc(db, 'statuses', s.id), { name: s.name, dot: s.dot, color: s.color, order: s.order, done: s.done, updatedBy: actorUid || null, updatedAt: serverTimestamp() }))
  await batch.commit()
}

// ── Reads/writes: engagements (speaking / PR pipeline) ────────────────────────

export function watchEngagements(type, cb) {
  // Filtered client-side: a where('type','==',type) query would need a new
  // type+order composite index, and the collection is small.
  return onSnapshot(query(collection(db, 'engagements'), orderBy('order', 'asc')), (snap) =>
    cb(snapToDocs(snap).filter((e) => engagementType(e) === type)))
}
/** Unfiltered engagements across all boards — used by the Home dashboard for org-wide metrics. */
export function watchAllEngagements(cb) {
  return onSnapshot(collection(db, 'engagements'), (snap) => cb(snapToDocs(snap)))
}

export async function createEngagement(eng, actorUid) {
  const ref = await addDoc(collection(db, 'engagements'), {
    type: eng.type || 'speaking',
    title: eng.title || '',
    org: eng.org || '',
    stage: eng.stage || '',
    date: eng.date ? Timestamp.fromDate(eng.date) : null,
    endDate: eng.endDate ? Timestamp.fromDate(eng.endDate) : null,
    location: eng.location || '',
    fee: typeof eng.fee === 'number' ? eng.fee : null,
    feeStatus: eng.feeStatus || null,
    offerNotes: eng.offerNotes || '',
    contactName: eng.contactName || '',
    contactEmail: eng.contactEmail || '',
    contactOrg: eng.contactOrg || '',
    links: eng.links || [],
    ownerUid: eng.ownerUid || null,
    notes: eng.notes || '',
    order: typeof eng.order === 'number' ? eng.order : Date.now(),
    calendarEventId: null,
    calendarSyncedAt: null,
    createdAt: serverTimestamp(),
    createdBy: actorUid || null,
    updatedBy: actorUid || null,
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateEngagement(id, patch, actorUid) {
  const data = { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() }
  if ('date' in patch) data.date = patch.date ? Timestamp.fromDate(patch.date) : null
  if ('endDate' in patch) data.endDate = patch.endDate ? Timestamp.fromDate(patch.endDate) : null
  await updateDoc(doc(db, 'engagements', id), data)
}

export async function moveEngagementToStage(id, stage, order, actorUid) {
  await updateDoc(doc(db, 'engagements', id), { stage, order, updatedBy: actorUid || null, updatedAt: serverTimestamp() })
}

export async function deleteEngagement(id) {
  await deleteDoc(doc(db, 'engagements', id))
}

// ── Reads/writes: engagement stages (board columns) ───────────────────────────

export function watchEngagementStages(type, cb) {
  return onSnapshot(query(collection(db, 'engagementStages'), orderBy('order', 'asc')), (snap) =>
    cb(snapToDocs(snap).filter((s) => s.type === type)))
}

/** Every engagement stage across all types — Home reads this for the forecast. */
export function watchAllEngagementStages(cb) {
  return onSnapshot(query(collection(db, 'engagementStages'), orderBy('order', 'asc')), (snap) => cb(snapToDocs(snap)))
}

export async function createEngagementStage(s, actorUid) {
  const ref = await addDoc(collection(db, 'engagementStages'), {
    type: s.type || 'speaking',
    name: s.name || 'New stage',
    dot: s.dot || '#7A7568',
    color: s.color || '#7A7568',
    order: typeof s.order === 'number' ? s.order : Date.now(),
    done: !!s.done,
    probability: typeof s.probability === 'number' ? s.probability : 0,
    outcome: s.outcome || null,
    updatedBy: actorUid || null,
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateEngagementStage(id, patch, actorUid) {
  await updateDoc(doc(db, 'engagementStages', id), { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() })
}

export async function deleteEngagementStage(id) {
  await deleteDoc(doc(db, 'engagementStages', id))
}

/** Seed default engagement stages (both boards) once, when the collection is empty. */
export async function seedDefaultEngagementStages(actorUid) {
  const existing = await getDocs(collection(db, 'engagementStages'))
  if (!existing.empty) return
  const batch = writeBatch(db)
  DEFAULT_ENGAGEMENT_STAGES.forEach((s) => batch.set(doc(db, 'engagementStages', s.id), { type: s.type, name: s.name, dot: s.dot, color: s.color, order: s.order, done: s.done, probability: s.probability, outcome: s.outcome, updatedBy: actorUid || null, updatedAt: serverTimestamp() }))
  await batch.commit()
}

/** Tasks linked to an engagement as action items. */
export function watchEngagementTasks(engagementId, cb) {
  const q = query(collection(db, 'tasks'), where('engagementId', '==', engagementId))
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

// Wave 2: `deals`, `dealStages` and `dealPipelines` were folded into engagements.
// See docs/superpowers/specs/2026-09-01-kaycia-feedback-wave-2-design.md.

export function watchPropertyDefs(objectType, cb) {
  const q = query(collection(db, 'propertyDefs'), where('objectType', '==', objectType))
  return onSnapshot(q, (snap) => {
    const defs = snapToDocs(snap)
      .filter((d) => !d.archived)
      .sort((a, b) => (a.group || '').localeCompare(b.group || '') || (a.order || 0) - (b.order || 0))
    cb(defs)
  })
}

export async function createPropertyDef(def, actorUid) {
  const ref = await addDoc(collection(db, 'propertyDefs'), {
    objectType: def.objectType,
    key: def.key,
    label: def.label || 'New property',
    type: def.type || 'text',
    options: def.options || [],
    group: def.group || 'Custom',
    order: typeof def.order === 'number' ? def.order : Date.now(),
    isCore: !!def.isCore,
    required: !!def.required,
    archived: false,
    tenantId: 'careernomad',
    createdBy: actorUid || null,
    createdAt: serverTimestamp(),
    updatedBy: actorUid || null,
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updatePropertyDef(id, patch, actorUid) {
  await updateDoc(doc(db, 'propertyDefs', id), { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() })
}

export async function archivePropertyDef(id, actorUid) {
  await updateDoc(doc(db, 'propertyDefs', id), { archived: true, updatedBy: actorUid || null, updatedAt: serverTimestamp() })
}

/** Idempotently seed the core property defs at deterministic ids. */
export async function seedCorePropertyDefs(actorUid) {
  // Seed exactly the MISSING core defs (deterministic ids make this an
  // idempotent upsert). A coarse "any def exists" guard used to suppress ALL
  // core defs whenever a single stray/custom def was present — which is how
  // new contacts lost their name fields.
  const existing = await getDocs(collection(db, 'propertyDefs'))
  const have = new Set(existing.docs.map((d) => d.id))
  const missing = CORE_PROPERTY_DEFS.filter((d) => !have.has(`pdef-${d.objectType}-${d.key}`))
  if (missing.length === 0) return
  const batch = writeBatch(db)
  missing.forEach((d) => {
    batch.set(doc(db, 'propertyDefs', `pdef-${d.objectType}-${d.key}`), {
      ...d, archived: false, tenantId: 'careernomad', updatedBy: actorUid || null, updatedAt: serverTimestamp(),
    })
  })
  await batch.commit()
}

// â”€â”€ CRM: associations (many-to-many join, deterministic pair-key ids) â”€â”€â”€â”€â”€â”€

/** All associations touching `token` (e.g. 'contact:c1'), either direction. */
export function watchAssociations(token, cb) {
  const q = query(collection(db, 'associations'), where('endpoints', 'array-contains', token))
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

/** Create/merge a link. Idempotent via sorted pair-key doc id. */
export async function associate(tokenA, tokenB, label, actorUid) {
  const endpoints = [tokenA, tokenB].sort()
  const id = endpoints.join('__')
  await setDoc(doc(db, 'associations', id), {
    endpoints,
    a: endpoints[0],
    b: endpoints[1],
    label: label || null,
    tenantId: 'careernomad',
    createdBy: actorUid || null,
    createdAt: serverTimestamp(),
  }, { merge: true })
  return id
}

export async function unassociate(tokenA, tokenB) {
  const id = [tokenA, tokenB].sort().join('__')
  await deleteDoc(doc(db, 'associations', id))
}

/** Cascade helper: delete every association touching `token`. */
export async function deleteAssociationsFor(token) {
  const snap = await getDocs(query(collection(db, 'associations'), where('endpoints', 'array-contains', token)))
  if (snap.empty) return
  const batch = writeBatch(db)
  snap.forEach((d) => batch.delete(d.ref))
  await batch.commit()
}

/** Cascade helper: delete every timeline activity attached to a record. */
export async function deleteActivitiesFor(token) {
  const snap = await getDocs(query(collection(db, 'activities'), where('objectToken', '==', token)))
  if (snap.empty) return
  const batch = writeBatch(db)
  snap.forEach((d) => batch.delete(d.ref))
  await batch.commit()
}

// â”€â”€ CRM: contacts & companies â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function watchContacts(cb) {
  return onSnapshot(collection(db, 'contacts'), (snap) => {
    cb(snapToDocs(snap).sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '')))
  })
}
export function watchContact(id, cb) {
  return onSnapshot(doc(db, 'contacts', id), (d) => cb(d.exists() ? { id: d.id, ...d.data() } : null))
}
export function watchCompanies(cb) {
  return onSnapshot(collection(db, 'companies'), (snap) => {
    cb(snapToDocs(snap).sort((a, b) => (a.name || '').localeCompare(b.name || '')))
  })
}
export function watchCompany(id, cb) {
  return onSnapshot(doc(db, 'companies', id), (d) => cb(d.exists() ? { id: d.id, ...d.data() } : null))
}

/** Create a contact, enforcing email uniqueness atomically. Throws 'duplicate-email'. */
export async function createContact(contact, actorUid) {
  const emailLower = normalizeEmail(contact.email)
  const ref = doc(collection(db, 'contacts'))
  await runTransaction(db, async (tx) => {
    if (emailLower) {
      const keyRef = doc(db, 'contactKeys', emailLower)
      const existing = await tx.get(keyRef)
      if (existing.exists()) throw new Error('duplicate-email')
      tx.set(keyRef, { contactId: ref.id })
    }
    tx.set(ref, {
      firstName: contact.firstName || '', lastName: contact.lastName || '',
      email: contact.email || '', emailLower,
      phone: contact.phone || '', title: contact.title || '',
      primaryCompanyId: contact.primaryCompanyId || null, ownerUid: contact.ownerUid || null,
      lifecycleStage: contact.lifecycleStage || null, source: contact.source || 'manual',
      custom: contact.custom || {}, tenantId: 'careernomad',
      createdBy: actorUid || null, createdAt: serverTimestamp(),
      updatedBy: actorUid || null, updatedAt: serverTimestamp(),
    })
  })
  return ref.id
}

export async function updateContact(id, patch, actorUid) {
  if (!('email' in patch)) {
    await updateDoc(doc(db, 'contacts', id), { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() })
    return
  }
  const newEmailLower = normalizeEmail(patch.email)
  await runTransaction(db, async (tx) => {
    const ref = doc(db, 'contacts', id)
    const cur = await tx.get(ref)
    const oldEmailLower = cur.exists() ? cur.data().emailLower : null
    if (newEmailLower !== oldEmailLower && newEmailLower) {
      const newKeyRef = doc(db, 'contactKeys', newEmailLower)
      const existing = await tx.get(newKeyRef)
      if (existing.exists()) throw new Error('duplicate-email')
    }
    if (newEmailLower !== oldEmailLower) {
      if (newEmailLower) tx.set(doc(db, 'contactKeys', newEmailLower), { contactId: id })
      if (oldEmailLower) tx.delete(doc(db, 'contactKeys', oldEmailLower))
    }
    tx.set(ref, { ...patch, emailLower: newEmailLower, updatedBy: actorUid || null, updatedAt: serverTimestamp() }, { merge: true })
  })
}

export async function deleteContact(id, actorUid) {
  const snap = await getDoc(doc(db, 'contacts', id))
  const emailLower = snap.exists() ? snap.data().emailLower : null
  await deleteAssociationsFor(objToken('contact', id))
  await deleteActivitiesFor(objToken('contact', id))
  // Mirror the company cascade: engagements carry a primaryContactId FK (unused
  // by the UI today, but the moment a future increment writes it, an uncleaned
  // reference would dangle silently).
  const [depEngagements, depSubs] = await Promise.all([
    getDocs(query(collection(db, 'engagements'), where('primaryContactId', '==', id))),
    // Form submissions hold the contact's PII — a data-deletion request must
    // not strand them (spec 2026-08-11 §3.3).
    getDocs(query(collection(db, 'formSubmissions'), where('contactId', '==', id))),
  ])
  const batch = writeBatch(db)
  depEngagements.forEach((d) => batch.update(d.ref, { primaryContactId: null, updatedBy: actorUid || null, updatedAt: serverTimestamp() }))
  depSubs.forEach((d) => batch.delete(d.ref))
  if (emailLower) batch.delete(doc(db, 'contactKeys', emailLower))
  batch.delete(doc(db, 'contacts', id))
  await batch.commit()
}

/** Create a company, enforcing domain uniqueness atomically. Throws 'duplicate-domain'. */
export async function createCompany(company, actorUid) {
  const domainKey = normalizeDomain(company.domain)
  const ref = doc(collection(db, 'companies'))
  await runTransaction(db, async (tx) => {
    if (domainKey) {
      const keyRef = doc(db, 'companyKeys', domainKey)
      const existing = await tx.get(keyRef)
      if (existing.exists()) throw new Error('duplicate-domain')
      tx.set(keyRef, { companyId: ref.id })
    }
    tx.set(ref, {
      name: company.name || '', domain: company.domain || '', domainKey,
      industry: company.industry || null, size: company.size || null,
      city: company.city || '', website: company.website || '',
      ownerUid: company.ownerUid || null, custom: company.custom || {}, tenantId: 'careernomad',
      createdBy: actorUid || null, createdAt: serverTimestamp(),
      updatedBy: actorUid || null, updatedAt: serverTimestamp(),
    })
  })
  return ref.id
}

export async function updateCompany(id, patch, actorUid) {
  if (!('domain' in patch)) {
    await updateDoc(doc(db, 'companies', id), { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() })
    return
  }
  const newDomainKey = normalizeDomain(patch.domain)
  await runTransaction(db, async (tx) => {
    const ref = doc(db, 'companies', id)
    const cur = await tx.get(ref)
    const oldDomainKey = cur.exists() ? cur.data().domainKey : null
    if (newDomainKey !== oldDomainKey && newDomainKey) {
      const newKeyRef = doc(db, 'companyKeys', newDomainKey)
      const existing = await tx.get(newKeyRef)
      if (existing.exists()) throw new Error('duplicate-domain')
    }
    if (newDomainKey !== oldDomainKey) {
      if (newDomainKey) tx.set(doc(db, 'companyKeys', newDomainKey), { companyId: id })
      if (oldDomainKey) tx.delete(doc(db, 'companyKeys', oldDomainKey))
    }
    tx.set(ref, { ...patch, domainKey: newDomainKey, updatedBy: actorUid || null, updatedAt: serverTimestamp() }, { merge: true })
  })
}

export async function deleteCompany(id, actorUid) {
  const snap = await getDoc(doc(db, 'companies', id))
  const domainKey = snap.exists() ? snap.data().domainKey : null
  await deleteAssociationsFor(objToken('company', id))
  await deleteActivitiesFor(objToken('company', id))
  // Clear back-references so dependent records don't point at a ghost company.
  const [depContacts, depEngagements] = await Promise.all([
    getDocs(query(collection(db, 'contacts'), where('primaryCompanyId', '==', id))),
    getDocs(query(collection(db, 'engagements'), where('primaryCompanyId', '==', id))),
  ])
  const batch = writeBatch(db)
  depContacts.forEach((d) => batch.update(d.ref, { primaryCompanyId: null, updatedBy: actorUid || null, updatedAt: serverTimestamp() }))
  depEngagements.forEach((d) => batch.update(d.ref, { primaryCompanyId: null, updatedBy: actorUid || null, updatedAt: serverTimestamp() }))
  if (domainKey) batch.delete(doc(db, 'companyKeys', domainKey))
  batch.delete(doc(db, 'companies', id))
  await batch.commit()
}

// â”€â”€ CRM: activity timeline (append-only) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function watchActivities(objectToken, cb) {
  const q = query(collection(db, 'activities'), where('objectToken', '==', objectToken), orderBy('createdAt', 'desc'))
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

export async function logActivity(objectToken, { type, body, meta }, actorUid) {
  const ref = await addDoc(collection(db, 'activities'), {
    objectToken, type: type || 'note', body: body || '', meta: meta || {},
    actorUid: actorUid || null, createdAt: serverTimestamp(),
  })
  return ref.id
}

// ── Reads/writes: saved views (named, reusable filter presets) ───────────────

/** The signed-in user's saved views, alphabetical by name. */
export function watchMySavedViews(uid, cb) {
  const q = query(
    collection(db, 'savedViews'),
    where('ownerUid', '==', uid),
    orderBy('name', 'asc'),
  )
  return onSnapshot(q, (snap) => cb(snapToDocs(snap)))
}

export async function createSavedView(v, actorUid) {
  const ref = await addDoc(collection(db, 'savedViews'), {
    name: v.name || '',
    scope: v.scope || '',
    viewType: v.viewType || '',
    filters: v.filters || {},
    ownerUid: actorUid,
    updatedBy: actorUid,
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function deleteSavedView(id) {
  await deleteDoc(doc(db, 'savedViews', id))
}

// ── helpers ───────────────────────────────────────────────────────────────────

export function statusLabel(status) {
  return { todo: 'To Do', in_progress: 'In Progress', done: 'Done' }[status] || status
}

export { deleteField, getDocs, collectionGroup }

// ── Reads/writes: app settings (Phase 5 Google integration) ──────────────────

/** Watch the Google-integration settings doc (shared calendar id, …). */
export function watchGoogleSettings(cb) {
  return onSnapshot(doc(db, 'settings', 'google'), (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null))
}

/** Merge-save Google-integration settings, stamped by the acting user. */
export async function saveGoogleSettings(patch, actorUid) {
  await setDoc(doc(db, 'settings', 'google'), { ...patch, updatedBy: actorUid || null, updatedAt: serverTimestamp() }, { merge: true })
}

/**
 * Stamp a row's calendar sync WITHOUT touching updatedAt — staleness is
 * defined as updatedAt > calendarSyncedAt, so the stamp must not bump the
 * edit clock. updatedBy is set to satisfy the stampedBySelf rules posture.
 */
export async function stampCalendarSync(collectionName, id, eventId, actorUid) {
  await updateDoc(doc(db, collectionName, id), {
    calendarEventId: eventId,
    calendarSyncedAt: serverTimestamp(),
    updatedBy: actorUid || null,
  })
}
